import React, { memo, useEffect, useMemo } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { validators, formats, dates } from "investira.sdk";
import {
  Button,
  Icon,
  Stack,
  Typography,
  IconButton,
  Tooltip,
  Chip,
} from "investira.react.components";
import { ReportsTemplate } from "../../components/templates";
import { useRelatorioContext } from "../../contexts/RelatorioContext";
import { OUTPUT } from "../../enums";
import ReportParamsDialog from "./_components/ReportParamsDialog";
import withOutput from "../../hoc/withOutput";

const ReportsOutputsConsulta = memo((props) => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  params.output = "enquadramento";

  const { actions } = useRelatorioContext();

  const carteiras = useSelector((store) => store.carteiras.data);
  const enquadramentos = useSelector(
    (store) => store.reports.enquadramentos.data
  );

  const xColumns = useMemo(
    () => [
      {
        id: "criacao",
        accessorKey: "created",
        header: "Criação",
        enableColumnActions: false,
        enableSorting: false,
        Cell: ({ cell }) => {
          if (validators.isNull(cell.getValue())) {
            return null;
          }
          return formats.formatDate(dates.toDate(cell.getValue()));
        },
      },
      {
        id: "carteira",
        accessorKey: "request",
        header: "Carteira",
        enableColumnActions: false,
        enableSorting: false,
        Cell: ({ cell }) => {
          if (validators.isNull(cell.getValue().carteira_id)) {
            return;
          }
          return carteiras.find(
            (carteira) => carteira.carteira_id === cell.getValue().carteira_id
          )?.carteira;
        },
      },
      {
        id: "posicao",
        accessorKey: "request",
        header: "Data Posição",
        enableColumnActions: false,
        enableSorting: false,
        Cell: ({ cell }) => {
          if (validators.isNull(cell.getValue().data)) {
            return;
          }
          return formats.formatDate(dates.toDate(cell.getValue().data));
        },
      },
      {
        id: "enquadramento",
        accessorKey: "request",
        header: "Enquadramento",
        enableColumnActions: false,
        enableSorting: false,
        muiTableHeadCellProps: {
          sx: {
            width: "100% !important",
          },
        },
        Cell: ({ cell }) => {
          if (validators.isNull(cell.getValue().enquadramento_id)) {
            return;
          }

          return enquadramentos.find(
            (enquadramento) =>
              enquadramento.enquadramento_id ===
              cell.getValue().enquadramento_id
          )?.enquadramento;
        },
      },
      // {
      //   id: "data",
      //   accessorKey: "data",
      //   header: "Data",
      //   enableColumnActions: false,
      //   enableSorting: false,
      //   Cell: ({ cell }) => {
      //     if (validators.isNull(cell.getValue())) {
      //       return;
      //     }
      //     return formats.formatDate(dates.toDate(cell.getValue()));
      //   },
      // },
      // {
      //   id: "outputcacheid",
      //   accessorKey: "output_cache_id",
      //   header: "ID",
      //   enableColumnActions: false,
      //   enableSorting: false,
      // },
      {
        id: "error",
        accessorKey: "error",
        header: "Status",
        enableColumnActions: false,
        enableSorting: false,
        Cell: (xProps) => {
          const { cell, row } = xProps;

          if (!validators.isNull(cell.getValue())) {
            const xError = row.original.error;
            return (
              <Tooltip
                arrow
                title={
                  <Stack
                    spacing={1}
                    sx={{
                      px: 3,
                      py: 2,

                      whiteSpace: "normal",
                    }}
                  >
                    <Stack spacing={1}>
                      <Typography variant="caption" color="textPrimary">
                        {xError}
                      </Typography>
                    </Stack>
                  </Stack>
                }
              >
                <span>
                  <Chip size="small" label="Erro" color="error" />
                </span>
              </Tooltip>
            );
          }

          return <Chip size="small" label="Ok" color="success" />;
        },
      },
    ],
    [enquadramentos, carteiras]
  );

  const renderNew = () => {
    return (
      <Button
        disabled={validators.isEmpty(carteiras)}
        variant="contained"
        color="primary"
        startIcon={<Icon iconName="insert" size={16} />}
        size="small"
        sx={{ px: 2 }}
        onClick={actions.handleNewConsult}
      >
        Nova Consulta
      </Button>
    );
  };

  const renderSettings = () => {
    return (
      <Stack flexGrow={0}>
        <Tooltip title={`Configurar ${OUTPUT[params.output].descricao}`}>
          <IconButton
            disabled={validators.isEmpty(carteiras)}
            color="primary"
            size="small"
            onClick={() => navigate(`${location.pathname}/configuracoes`)}
          >
            <Icon iconName="gear" size={24} />
          </IconButton>
        </Tooltip>
      </Stack>
    );
  };

  useEffect(() => {
    actions.handleOutputUpdate(params.output);
  }, [params.output]);

  return (
    <>
      <ReportsTemplate
        subtitle="Relatórios"
        title={OUTPUT[params.output].descricao}
        headerRenderActions={
          <Stack direction="row" spacing={2}>
            {validators.isEmpty(carteiras) ? (
              <>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Icon iconName="warning" color="warn" size={21} />

                  <Typography color="textPrimary" variant="caption">
                    Necessário possuir uma carteira favorita
                  </Typography>
                </Stack>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<Icon iconName="wallet" size={16} />}
                  size="small"
                  sx={{ px: 2 }}
                  onClick={() => navigate("/carteiras")}
                >
                  Ir para Carteiras
                </Button>
              </>
            ) : (
              <>
                {renderNew()}
                {renderSettings()}
              </>
            )}
          </Stack>
        }
        dataGridProps={{
          output: OUTPUT[params.output],
          columns: xColumns,
          data: props.caches,
          readData: props.readCacheList,
          isLoading: props.isLoading,
          initialState: {
            pagination: props.pagination,
            //columnVisibility: { carteira: false },
          },
          //manualFiltering: true,
          //manualSorting: true,
          manualPagination: true,
          getRowId: (row) => row.log_id,
          state: {
            isLoading: props.isLoading,
            pagination: props.pagination,
            showProgressBars: props.isRefetching,
            //columnFilters: props.columnFilters,
            //sorting: props.sorting,
            rowSelection: props.rowSelection,
          },
          rowCount: props.rowCount,
          //onColumnFiltersChange: props.setColumnFilters,
          //onSortingChange: props.setSorting,
          onPaginationChange: props.setPagination,
          onRowSelectionChange: props.setRowSelection,
          //onGlobalFilterChange: props.setGlobalFilter,
          onBulkDeleteClick: props.bulkDeleteCaches,
        }}
      />
      <ReportParamsDialog carteiras={carteiras} output={params.output} />
    </>
  );
});

ReportsOutputsConsulta.displayName = "ReportsOutputsConsulta";

export default withOutput(ReportsOutputsConsulta);
